import { ref, watch, unref } from 'vue';

// Utils for DOM manipulation
function scrollToTargetElement(target, containerEl, position) {
  const isElement = target && target.nodeType === 1;

  if (isElement && !position) {
    containerEl.scrollTop = target.offsetTop;
  } else if (position === 'bottom') {
    containerEl.scrollTop = containerEl.scrollHeight - containerEl.offsetHeight;
  } else if (position === 'top') {
    containerEl.scrollTop = 0;
  }
}

function getNode(selector) {
  if (typeof selector === 'string') {
    return document.querySelector(selector);
  }
  // Handle Vue component refs
  return selector && selector.$el ? selector.$el : selector;
}

// Fast synchronous visibility check using getBoundingClientRect
function isElementFullyVisible(element, container) {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  // Calculate relative positions
  const elementTop = elementRect.top - containerRect.top;
  const elementBottom = elementRect.bottom - containerRect.top;
  const containerHeight = containerRect.height;

  // Element is fully visible if both top and bottom are within container bounds
  return elementTop >= 0 && elementBottom <= containerHeight;
}

/**
 * Composable for keyboard navigation and selection in list-based UIs
 *
 * Handles:
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - Auto cursor reset when search query or items change
 * - Scroll adjustment to keep selected item visible
 * - Initial cursor positioning
 *
 * Use cases: Dropdowns, Autocomplete, Menus, Table navigation, Command palettes
 *
 * @param {object} config
 * @param {import('vue').Ref<any[]>|any[]} config.items - Reactive array of items to navigate (required)
 * @param {import('vue').Ref<string>|string} [config.searchQuery] - Optional search query for auto-reset on change
 * @param {import('vue').Ref<HTMLElement|null>} [config.containerRef] - Ref to scrollable container for scroll adjustment
 * @param {function(any): void} [config.onSelect] - Callback when item is selected (Enter key)
 * @param {function(): void} [config.onClose] - Callback when Escape key is pressed
 * @param {number} [config.initialCursorPosition=0] - Initial cursor position
 * @param {string} [config.hoverAttribute='data-hover'] - Attribute used to identify hovered element
 *
 * @returns {{
 *   cursorPosition: import('vue').Ref<number>,
 *   handleKeydown: (event: KeyboardEvent) => void,
 *   resetCursor: () => void,
 *   adjustScrollPosition: () => void,
 *   getCurrentItem: () => any,
 *   setCursorPosition: (index: number) => void,
 *   isItemHovered: (index: number) => boolean,
 * }}
 *
 * @example
 * // Basic usage in a dropdown component
 * const {
 *   cursorPosition,
 *   handleKeydown,
 *   isItemHovered,
 * } = useListSelection({
 *   items: computed(() => filteredOptions.value),
 *   searchQuery: query,
 *   containerRef: scrollContainerRef,
 *   onSelect: (item) => {
 *     emit('select', item);
 *     closeDropdown();
 *   },
 *   onClose: closeDropdown,
 * });
 */
export function useListSelection({
  items,
  searchQuery = null,
  containerRef = null,
  onSelect = null,
  onClose = null,
  initialCursorPosition = 0,
  hoverAttribute = 'data-hover',
}) {
  const cursorPosition = ref(initialCursorPosition);

  /**
   * Reset cursor position to first item
   */
  function resetCursor() {
    cursorPosition.value = 0;
  }

  /**
   * Set cursor to specific index (with bounds checking)
   */
  function setCursorPosition(index) {
    const itemsList = unref(items);
    if (!itemsList || itemsList.length === 0) return;

    const boundedIndex = Math.max(0, Math.min(index, itemsList.length - 1));
    cursorPosition.value = boundedIndex;
  }

  /**
   * Check if item at index is currently hovered
   */
  function isItemHovered(index) {
    return cursorPosition.value === index;
  }

  /**
   * Get currently selected item based on cursor position
   */
  function getCurrentItem() {
    const itemsList = unref(items);
    if (!itemsList || itemsList.length === 0) return null;

    return itemsList[cursorPosition.value];
  }

  /**
   * Adjust scroll position to keep selected item visible
   * Works with both native elements and Vue components (rendered to DOM)
   */
  function adjustScrollPosition() {
    if (!containerRef) return;

    // Get container element (handles Vue component refs)
    const container = getNode(unref(containerRef));
    if (!container || typeof container.querySelector !== 'function') {
      return;
    }

    const itemsList = unref(items);
    if (!itemsList || itemsList.length === 0) return;

    // Detect first or last position
    const isFirstPosition = cursorPosition.value === 0;
    const isLastPosition = cursorPosition.value === itemsList.length - 1;

    // Find selected element
    const selectedElement = container.querySelector(
      `[${hoverAttribute}="true"]`
    );

    if (!selectedElement) return;

    // Fast synchronous visibility check
    const isVisible = isElementFullyVisible(selectedElement, container);

    // Only scroll if not fully visible
    if (!isVisible) {
      // Determine scroll position based on cursor position
      let position;
      if (isFirstPosition) {
        position = 'top';
      } else if (isLastPosition) {
        position = 'bottom';
      }

      scrollToTargetElement(selectedElement, container, position);
    }
  }

  /**
   * Navigate to previous item (Arrow Up)
   */
  function onArrowUp() {
    const itemsList = unref(items);
    if (!itemsList || itemsList.length === 0) return;

    if (cursorPosition.value > 0) {
      cursorPosition.value -= 1;
      // Wait for DOM to update data-hover attribute
      setTimeout(() => {
        adjustScrollPosition();
      }, 50);
    }
  }

  /**
   * Navigate to next item (Arrow Down)
   */
  function onArrowDown() {
    const itemsList = unref(items);
    if (!itemsList || itemsList.length === 0) return;

    if (cursorPosition.value < itemsList.length - 1) {
      cursorPosition.value += 1;
      // Wait for DOM to update data-hover attribute
      setTimeout(() => {
        adjustScrollPosition();
      }, 50);
    }
  }

  /**
   * Select current item (Enter key)
   */
  function onEnter() {
    const currentItem = getCurrentItem();
    if (!currentItem) return;

    if (onSelect) {
      onSelect(currentItem);
    }
  }

  /**
   * Close dropdown/list (Escape key)
   */
  function onEscape() {
    if (onClose) {
      onClose();
    }
  }

  /**
   * Main keyboard event handler
   */
  function handleKeydown(event) {
    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        onArrowUp();
        break;
      case 'ArrowDown':
        event.preventDefault();
        onArrowDown();
        break;
      case 'Enter':
        event.preventDefault();
        onEnter();
        break;
      case 'Escape':
        event.preventDefault();
        onEscape();
        break;
      default:
        break;
    }
  }

  /**
   * Watch items and search query for changes
   * Auto-reset cursor position when they change
   * This fixes the bug where cursor can point to invalid index after filtering
   */
  watch(
    () => [unref(items), unref(searchQuery)],
    ([newItems, newQuery], [oldItems, oldQuery]) => {
      // Reset cursor if search query changes
      if (searchQuery !== null && newQuery !== oldQuery) {
        resetCursor();
        return;
      }

      // Reset cursor if items list changes (length or content)
      if (newItems !== oldItems) {
        // Ensure cursor is within valid bounds
        const maxIndex = newItems?.length ? newItems.length - 1 : 0;
        if (cursorPosition.value > maxIndex) {
          cursorPosition.value = Math.max(0, maxIndex);
        }
      }
    },
    { immediate: false }
  );

  return {
    cursorPosition,
    handleKeydown,
    resetCursor,
    adjustScrollPosition,
    getCurrentItem,
    setCursorPosition,
    isItemHovered,
  };
}
